<script>
    export const DISPOSITIONS_CONFIG = {
        lead: [
            {
                value: 'new',
                label: 'New',
                type: 'green',
            },
            {
                value: 'attempted_contact',
                label: 'Attempted Contact',
                type: 'green',
            },
            {
                value: 'connected',
                label: 'Connected',
                type: 'green',
            },
            {
                value: 'unresponsive',
                label: 'Unresponsive',
                type: 'orange',
            },
            {
                value: 'disqualified',
                label: 'Disqualified',
                type: 'red',
            },
        ],
        prospect: [
            {
                value: 'searching',
                label: 'Searching',
                type: 'blue',
            },
            {
                value: 'interviewing',
                label: 'Interviewing',
                type: 'blue',
            },
            {
                value: 'waiting',
                label: 'Waiting',
                type: 'black',
            },
            {
                value: 'on_hold',
                label: 'On Hold',
                type: 'orange',
            },
            {
                value: 'unresponsive',
                label: 'Unresponsive',
                type: 'orange',
            },
            {
                value: 'no_opportunity',
                label: 'No Opportunity',
                type: 'red',
            },
        ],
        client: [
            {
                value: 'service_starting',
                label: 'Service Starting',
                type: 'green',
            },
            {
                value: 'ongoing',
                label: 'Ongoing',
                type: 'green',
            },
            {
                value: 'completed',
                label: 'Completed',
                type: 'green',
            },
            {
                value: 'paused',
                label: 'Paused',
                type: 'orange',
            },
            {
                value: 'terminated',
                label: 'Terminated',
                type: 'red',
            }
        ],
    }

    /**
     * The status dot colour for each disposition colour, as the picker's button shows it.
     */
    export const DISPOSITION_DOT_CLASSES = {
        red: 'bg-lifeworx-red-400',
        blue: 'bg-lifeworx-blue-500',
        orange: 'bg-lifeworx-orange-400',
        green: 'bg-lifeworx-green-400',
        black: 'bg-lifeworx-blue-900',
    }

    /**
     * The label colour for each disposition colour, as the picker's button shows it.
     */
    export const DISPOSITION_TEXT_CLASSES = {
        red: 'text-lifeworx-red-500',
        blue: 'text-lifeworx-blue-600',
        orange: 'text-lifeworx-orange-600',
        green: 'text-lifeworx-green-600',
        black: 'text-lifeworx-blue-900',
    }

    /**
     * The colour (green, blue, orange, red or black) of a client type's disposition, or null when it is unknown.
     */
    export function dispositionColor(clientType, disposition) {
        return DISPOSITIONS_CONFIG[clientType]?.find((option) => option.value === disposition)?.type ?? null
    }
</script>

<script setup>

    import { ref, computed } from 'vue'
    import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
    import { ChevronUpDownIcon } from '@heroicons/vue/16/solid'
    import { CheckIcon, ClockIcon, XMarkIcon } from '@heroicons/vue/20/solid'

    const props = defineProps({
        modelValue: {
            required: true
        },
        theme: {
            type: String,
            default: 'default',
            validator: (theme) => ['default', 'button'].includes(theme)
        },
        disabled: {
            type: Boolean,
            default: false
        }
    })

    const emit = defineEmits(['update:modelValue']);

    // Get the current selected client type based on modelValue.type
    const selectedClientType = computed(() => {
        if (props.modelValue?.clientType === 'lead' || !props.modelValue?.clientType) {
            return 'ld';
        } else if (props.modelValue?.clientType === 'prospect') {
            return 'pr';
        } else if (props.modelValue?.clientType === 'client') {
            return 'cl';
        }
    })

    // Internal value for the Listbox v-model - now works with individual disposition objects
    const internalValue = computed({
        get: () => {
            // Return the currently selected disposition object
            return selectedDisposition.value;
        },
        set: (dispositionObject) => {
            if (dispositionObject && dispositionObject.value && dispositionObject.clientType) {
                // Update both disposition and type based on the selected disposition
                emit('update:modelValue', {
                    ...props.modelValue,
                    disposition: dispositionObject.value,
                    clientType: dispositionObject.clientType
                });
            }
        },
    })

    // Get all disposition options as a flat array with client type included
    const allDispositionOptions = computed(() => {
        return Object.entries(DISPOSITIONS_CONFIG).flatMap(([clientType, dispositions]) =>
            dispositions.map(disposition => ({ ...disposition, clientType }))
        );
    })

    // Find the current selected disposition object based on modelValue.disposition
    const selectedDisposition = computed(() => {
        const dispositionValue = props.modelValue?.disposition;
        const clientType = props.modelValue?.clientType;
        return allDispositionOptions.value.find(disposition => disposition.value === dispositionValue && disposition.clientType === clientType) || null;
    })

    const buttonTheme = computed(() => {
        switch (props.theme) {
            case 'button':
                return [
                    'grid w-full cursor-default bg-white/50 xhover:bg-lifworx-blue-200/10 grid-cols-1 rounded-md py-1.5 pl-2 pr-2 text-left text-stone-900 outline outline-1 -outline-offset-1 outline-stone-300/25 hover:outline-stone-400 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6'
                ];
            case 'default':
            default:
                return [
                    'grid w-full cursor-default xbg-lifeworx-alabaster-200/50 hover:bg-lifeworx-alabaster-200 grid-cols-1 rounded-md py-1.5 pl-2 pr-2 text-left text-stone-900 outline outline-1 -outline-offset-1 outline-stone-300 hover:outline-stone-400 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6'
                ];
        }
    })

</script>

<template>

    <Listbox as="div" v-model="internalValue" id="client-disposition">
        <ListboxLabel
            v-if="props.theme === 'default'"
            class="text-xs font-semibold text-stone-500"
            for="client-disposition"
        >Disposition</ListboxLabel>
        <div class="relative">

            <ListboxButton
                :class="[
                    selectedDisposition?.type === 'red' ? 'bg-lifeworx-red-400/10' : '',
                    selectedDisposition?.type === 'blue' ? 'bg-lifeworx-blue-500/10' : '',
                    selectedDisposition?.type === 'orange' ? 'bg-lifeworx-orange-400/10' : '',
                    selectedDisposition?.type === 'green' ? 'bg-lifeworx-green-400/10' : '',
                    selectedDisposition?.type === 'black' ? 'bg-lifeworx-blue-900/10' : '',
                    selectedDisposition ? null : 'border-[1.5px] border-lifeworx-red-500/75',
                    buttonTheme
                ]"
                :disabled="props.disabled"
                @click.stop
            >
                <span class="col-start-1 row-start-1 flex items-center gap-2 pr-6">
                     <span
                        :class="[
                            props.theme === 'button' ? ' min-w-5' : '',
                            'block truncate'
                        ]"
                    >
                        <span
                            :class="[
                                'font-mono font-semibold xl:ml-2 capitalize',
                                DISPOSITION_TEXT_CLASSES[selectedDisposition?.type] ?? 'text-stone-300'
                            ]"
                        >{{ selectedClientType }}
                        </span>
                    </span>
                    <span
                        :class="[
                            DISPOSITION_DOT_CLASSES[selectedDisposition?.type] ?? 'bg-stone-300',
                            'inline-block ml-2 size-3 shrink-0 rounded-full border border-transparent'
                        ]"
                    />
                    <span class="block truncate font-bold ">
                        <span
                            :class="[
                                DISPOSITION_TEXT_CLASSES[selectedDisposition?.type] ?? 'text-stone-300'
                            ]"
                        >{{ selectedDisposition?.label ? selectedDisposition.label : 'Unknown' }}</span>
                    </span>
                </span>
                <ChevronUpDownIcon
                    class="col-start-1 row-start-1 size-5 self-center justify-self-end text-stone-500 sm:size-4"
                    aria-hidden="true"
                />
            </ListboxButton>

            <transition
                leave-active-class="transition ease-in duration-100"
                leave-from-class=""
                leave-to-class="opacity-0"
            >
                <ListboxOptions
                    class="absolute z-popover mt-1 md:min-w-full lg:min-w-[60rem] overflow-auto rounded-lg bg-white py-1 text-base shadow-lg outline outline-1 outline-black/5 sm:text-sm"
                >
                    <div class="px-5 py-4 grid gap-2">
                        <template
                            v-for="(dispositions, clientType) in DISPOSITIONS_CONFIG"
                            :key="clientType"
                        >
                            <div class="grid grid-cols-1 lg:grid-cols-5 gap-1">
                                <div class="col-span-1 items-center">
                                    <div class="text-xs font-semibold text-gray-700 capitalize">
                                        {{ clientType }}
                                    </div>
                                </div>

                                <div class="col-span-5 flex gap-2">

                                    <div
                                        v-if="dispositions.filter(s => s.type === 'blue').length > 0"
                                        class="flex grow wrap py-1 px-4 items-center justify-center gap-2 rounded-lg bg-lifeworx-blue-400/20"
                                    >
                                        <ListboxOption
                                            as="template"
                                            v-for="(disposition, key) in dispositions.filter(s => s.type === 'blue')"
                                            :value="allDispositionOptions.find(option => option.value === disposition.value && option.clientType === clientType)"
                                            v-slot="{ active, selected }"
                                        >
                                            <div
                                                :class="[
                                                    active ? 'bg-lifeworx-blue-500 text-white outline-none' : 'text-stone-900',
                                                    selected ? 'ring-2 ring-inset ring-lifeworx-blue-500 bg-lifeworx-blue-500 text-white' : '',
                                                    'relative cursor-default select-none px-3 py-1 rounded-md'
                                                ]"
                                            >
                                                <div class="flex items-center gap-2">
                                                    <span v-if="selected">
                                                        <CheckIcon class="size-3 text-white" aria-hidden="true" />
                                                    </span>
                                                    <span
                                                        v-else
                                                        :class="[
                                                            'bg-lifeworx-blue-400 inline-block size-1.5 shrink-0 rounded-full border border-transparent'
                                                        ]"
                                                        aria-hidden="true"
                                                    />
                                                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'text-xs whitespace-nowrap']">
                                                        {{ disposition.label }}
                                                    </span>

                                                </div>
                                            </div>
                                        </ListboxOption>
                                    </div>

                                    <div
                                        v-if="dispositions.filter(s => s.type === 'green').length > 0"
                                        class="flex grow py-1 px-4 items-center justify-center gap-2 rounded-lg bg-lifeworx-green-400/20"
                                    >
                                        <ListboxOption
                                            as="template"
                                            v-for="(disposition, key) in dispositions.filter(s => s.type === 'green')"
                                            :value="allDispositionOptions.find(option => option.value === disposition.value && option.clientType === clientType)"
                                            v-slot="{ active, selected }"
                                        >
                                            <div
                                                :class="[
                                                    active ? 'bg-lifeworx-green-500 text-white outline-none' : 'text-stone-900',
                                                    selected ? 'ring-2 ring-inset ring-lifeworx-green-500 bg-lifeworx-green-500 text-white' : '',
                                                    'relative cursor-default select-none px-3 py-1 rounded-md'
                                                ]"
                                            >
                                                <div class="flex items-center gap-2">
                                                    <span v-if="selected">
                                                        <CheckIcon class="size-3 text-white" aria-hidden="true" />
                                                    </span>
                                                    <span
                                                        v-else
                                                        :class="[
                                                            'bg-lifeworx-green-400 inline-block size-1.5 shrink-0 rounded-full border border-transparent'
                                                        ]"
                                                        aria-hidden="true"
                                                    />
                                                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'text-xs whitespace-nowrap']">
                                                        {{ disposition.label }}
                                                    </span>

                                                </div>
                                            </div>
                                        </ListboxOption>
                                    </div>

                                    <div
                                        v-if="dispositions.filter(s => s.type === 'black').length > 0"
                                        class="flex grow py-1 px-4 items-center justify-center gap-1 rounded-lg bg-lifeworx-blue-900/10"
                                    >
                                        <ListboxOption
                                            as="template"
                                            v-for="(disposition, key) in dispositions.filter(s => s.type === 'black')"
                                            :value="allDispositionOptions.find(option => option.value === disposition.value && option.clientType === clientType)"
                                            v-slot="{ active, selected }"
                                        >
                                            <div
                                                :class="[
                                                    active ? 'bg-lifeworx-blue-900 text-white outline-none' : 'text-stone-900',
                                                    selected ? 'ring-2 ring-inset ring-lifeworx-blue-900 bg-lifeworx-blue-900 text-white' : '',
                                                    'relative cursor-default select-none px-3 py-1 rounded-md'
                                                ]"
                                            >
                                                <div class="flex items-center gap-2">
                                                    <span v-if="selected">
                                                        <ClockIcon class="size-3 text-white" aria-hidden="true" />
                                                    </span>
                                                    <span
                                                        v-else
                                                        :class="[
                                                            'bg-lifeworx-blue-900 inline-block size-1.5 shrink-0 rounded-full border border-transparent'
                                                        ]"
                                                        aria-hidden="true"
                                                    />
                                                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'text-xs whitespace-nowrap']">
                                                        {{ disposition.label }}
                                                    </span>
                                                </div>
                                            </div>
                                        </ListboxOption>

                                    </div>

                                    <div
                                        v-if="dispositions.filter(s => s.type === 'orange').length > 0"
                                        class="flex grow py-1 px-4 items-center justify-center gap-1 rounded-lg bg-lifeworx-orange-400/20"
                                    >
                                        <ListboxOption
                                            as="template"
                                            v-for="(disposition, key) in dispositions.filter(s => s.type === 'orange')"
                                            :value="allDispositionOptions.find(option => option.value === disposition.value && option.clientType === clientType)"
                                            v-slot="{ active, selected }"
                                        >
                                            <div
                                                :class="[
                                                    active ? 'bg-lifeworx-orange-500 text-white outline-none' : 'text-stone-900',
                                                    selected ? 'ring-2 ring-inset ring-lifeworx-orange-500 bg-lifeworx-orange-500 text-white' : '',
                                                    'relative cursor-default select-none px-3 py-1 rounded-md'
                                                ]"
                                            >
                                                <div class="flex items-center gap-2">
                                                    <span v-if="selected">
                                                        <ClockIcon class="size-3 text-white" aria-hidden="true" />
                                                    </span>
                                                    <span
                                                        v-else
                                                        :class="[
                                                            'bg-lifeworx-orange-400 inline-block size-1.5 shrink-0 rounded-full border border-transparent'
                                                        ]"
                                                        aria-hidden="true"
                                                    />
                                                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'text-xs whitespace-nowrap']">
                                                        {{ disposition.label }}
                                                    </span>
                                                </div>
                                            </div>
                                        </ListboxOption>

                                    </div>

                                    <div
                                        v-if="dispositions.filter(s => s.type === 'red').length > 0"
                                        class="flex grow py-1 px-4 items-center justify-center gap-1 rounded-lg bg-lifeworx-red-400/20"
                                    >
                                        <ListboxOption
                                            as="template"
                                            v-for="(disposition, key) in dispositions.filter(s => s.type === 'red')"
                                            :value="allDispositionOptions.find(option => option.value === disposition.value && option.clientType === clientType)"
                                            v-slot="{ active, selected }"
                                        >
                                            <div
                                                :class="[
                                                    active ? 'bg-lifeworx-red-500 text-white outline-none' : 'text-stone-900 ',
                                                    selected ? 'ring-2 ring-inset ring-lifeworx-red-500 bg-lifeworx-red-500 text-white' : '',
                                                    'relative cursor-default select-none px-3 py-1 rounded-md'
                                                ]"
                                            >
                                                <div class="flex items-center gap-2">
                                                    <span v-if="selected">
                                                        <XMarkIcon class="size-3 text-white" aria-hidden="true" />
                                                    </span>
                                                    <span
                                                        v-else
                                                        :class="[
                                                            'bg-lifeworx-red-400 inline-block size-1.5 shrink-0 rounded-full border border-transparent'
                                                        ]"
                                                        aria-hidden="true"
                                                    />
                                                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'text-xs whitespace-nowrap']">
                                                        {{ disposition.label }}
                                                    </span>
                                                </div>
                                            </div>
                                        </ListboxOption>

                                    </div>

                                </div>
                            </div>
                        </template>
                    </div>
                </ListboxOptions>
            </transition>
        </div>
    </Listbox>
</template>
